import React from "react";
import { Link } from "react-router-dom";

import { Page } from "app/NativeComponents/common";
import { getNewDealMachineSignInUrl } from "app/NativeComponents/components/NewDealMachineBanner";

const migrationSteps = [
  {
    title: "Enter your Classic email",
    description:
      "On the New DealMachine sign-in page, enter the same email address you use for DealMachine Classic. This connects you to the account we prepared for you."
  },
  {
    title: "Use the one-time email",
    description:
      "We will send a one-time sign-in link or code to that email address. Open it to confirm that the account belongs to you."
  },
  {
    title: "Choose how you will sign in",
    description:
      "Connect Google or Apple sign-on, create a new password, or keep using email codes. Your Classic password cannot be transferred because DealMachine cannot view or recover it."
  },
  {
    title: "Start using the new DealMachine",
    description:
      "Your Classic account stays available. Your credits stay in sync, and any plan you buy in New DealMachine stacks on top of your current plan."
  }
];

const accountDetails = [
  {
    title: "Both accounts remain available",
    description:
      "You can keep working in DealMachine Classic while you get familiar with New DealMachine. Signing in does not cancel or replace your Classic account."
  },
  {
    title: "Your credits stay in sync",
    description:
      "Credits are shared between Classic and New DealMachine, so your available balance stays current across both accounts."
  },
  {
    title: "New plans stack with your current plan",
    description:
      "If you purchase an additional plan in New DealMachine, its seats and credits are added on top of the plan you already have."
  }
];

const NewDealMachineGuide = () => {
  return (
    <Page
      metaTitle="Move from DealMachine Classic to New DealMachine"
      metaDescription="Use your DealMachine Classic email to access New DealMachine, choose a new sign-in method, and understand how your account, credits, and plans work together."
    >
      <main className="new-dm-guide">
        <div className="new-dm-guide__background" aria-hidden="true">
          <span className="new-dm-guide__orb new-dm-guide__orb--left" />
          <span className="new-dm-guide__orb new-dm-guide__orb--right" />
          <span className="new-dm-guide__dots" />
        </div>

        <nav className="new-dm-guide__nav" aria-label="Guide navigation">
          <Link
            className="new-dm-guide__logo-link"
            to="/"
            aria-label="DealMachine Classic home"
          >
            <img
              className="new-dm-guide__logo"
              src="/assets/images/dm_white_logo_medium.png"
              alt="DealMachine"
            />
          </Link>
          <Link className="new-dm-guide__back-link" to="/">
            Back to Classic
          </Link>
        </nav>

        <div className="new-dm-guide__layout">
          <header className="new-dm-guide__hero">
            <span className="new-dm-guide__eyebrow">
              <span aria-hidden="true">&#10022;</span>
              Classic account guide
            </span>
            <h1>Use your Classic account in the new DealMachine</h1>
            <p>
              Start with the email you already use. We will connect you to your
              account and help you choose a secure way to sign in.
            </p>
            <a
              className="new-dm-guide__hero-button"
              href={getNewDealMachineSignInUrl("guide")}
            >
              Log in to new DealMachine
              <span aria-hidden="true">&rarr;</span>
            </a>
            <p className="new-dm-guide__reassurance">
              Your Classic account stays active. Signing in does not change your
              current plan.
            </p>
          </header>

          <section
            className="new-dm-guide__steps-card"
            aria-labelledby="migration-steps-title"
          >
            <div className="new-dm-guide__section-heading">
              <span>4 steps</span>
              <h2 id="migration-steps-title">What to expect</h2>
            </div>
            <ol className="new-dm-guide__steps">
              {migrationSteps.map((step, index) => (
                <li key={step.title}>
                  <span
                    className="new-dm-guide__step-number"
                    aria-hidden="true"
                  >
                    {index + 1}
                  </span>
                  <div>
                    <h3>{step.title}</h3>
                    <p>{step.description}</p>
                  </div>
                </li>
              ))}
            </ol>
          </section>
        </div>

        <section
          className="new-dm-guide__details"
          aria-labelledby="account-details-title"
        >
          <div className="new-dm-guide__details-heading">
            <span>Good to know</span>
            <h2 id="account-details-title">Your accounts work together</h2>
            <p>
              Explore the new experience without giving up the tools and plan
              you use today.
            </p>
          </div>
          <div className="new-dm-guide__detail-grid">
            {accountDetails.map((detail, index) => (
              <article key={detail.title}>
                <span className="new-dm-guide__detail-icon" aria-hidden="true">
                  {index === 0 ? "2" : index === 1 ? "\u21c4" : "+"}
                </span>
                <h3>{detail.title}</h3>
                <p>{detail.description}</p>
              </article>
            ))}
          </div>
        </section>

        <section
          className="new-dm-guide__final-cta"
          aria-labelledby="guide-cta-title"
        >
          <div>
            <span>Ready when you are</span>
            <h2 id="guide-cta-title">See what is new in DealMachine</h2>
            <p>
              Use your Classic email to open the account that is already waiting
              for you.
            </p>
          </div>
          <a href={getNewDealMachineSignInUrl("guide")}>
            Log in to new DealMachine
            <span aria-hidden="true">&rarr;</span>
          </a>
        </section>
      </main>
    </Page>
  );
};

export default NewDealMachineGuide;
